import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { identifyUser, resetAnalytics } from '../lib/analytics'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const userIdRef = useRef(null)

  useEffect(() => {
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      const initialUser = session?.user ?? null
      userIdRef.current = initialUser?.id ?? null
      setUser(initialUser)
      if (initialUser) identifyUser({ id: initialUser.id, email: initialUser.email, role: initialUser.app_metadata?.role })
      setLoading(false)
    })

    // Listen for changes on auth state (sign in, sign out, etc.)
    //
    // DUB-75: skip ALL same-user events, not just SIGNED_IN. Supabase fires
    // TOKEN_REFRESHED periodically (default ~1 hour) with a fresh session
    // object — same user, new reference. Calling setUser on those events
    // cascades a new authUser reference into UserContext, which flips
    // userLoading=true and refetches the profile. While userLoading=true,
    // ProtectedRoute renders its spinner, unmounting the entire route —
    // including the seed editor. When it remounts, fetchBracket re-reads
    // the DB and wipes out any unsaved in-progress matches the admin had
    // typed. The fix: only setUser when the user identity actually changes.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const newUser = session?.user ?? null
      const newUserId = newUser?.id ?? null

      // Same user, non-sign-out event (TOKEN_REFRESHED, redundant SIGNED_IN,
      // INITIAL_SESSION already handled by the getSession() above, etc.) —
      // no downstream state needs to change.
      if (event !== 'SIGNED_OUT' && newUserId === userIdRef.current) {
        return
      }

      userIdRef.current = newUserId
      setUser(newUser)
      if (event === 'SIGNED_IN' && newUser) {
        identifyUser({ id: newUser.id, email: newUser.email, role: newUser.app_metadata?.role })
        // A fresh login always starts in player mode. The admin-mode sticky
        // flag (Layout.jsx, key 'dub_admin_mode_sticky') survives reloads and
        // new tabs by design (DUB-67), but a sign-in is a context reset — if
        // an admin closed the browser while in admin mode, the next login
        // should land them on /home with the player nav, not silently
        // restore the prior session's admin context.
        try { localStorage.removeItem('dub_admin_mode_sticky') } catch { /* ignore */ }
      } else if (event === 'SIGNED_OUT') {
        resetAnalytics()
      }
      setLoading(false)
    })

    // Listen for deep links (OAuth redirects) on native platforms
    let appListener = null
    if (Capacitor.isNativePlatform()) {
      appListener = App.addListener('appUrlOpen', async ({ url }) => {
        if (url.startsWith('com.getdub.app://')) {
          try {
            // Close the browser if it was opened for auth
            await Browser.close().catch(() => {})

            // Handle URL parameters (PKCE or Implicit flow)
            const urlObj = new URL(url)
            const code = urlObj.searchParams.get('code')
            const error = urlObj.searchParams.get('error')
            const errorDescription = urlObj.searchParams.get('error_description')
            
            if (error) {
              console.error('[AuthContext] OAuth error:', error, errorDescription)
              throw new Error(errorDescription || error)
            }
            
            if (code) {
              const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
              if (exchangeError) {
                console.error('[AuthContext] Error exchanging code:', exchangeError)
                throw exchangeError
              }
            } else {
              // Fallback to checking hash for implicit flow tokens
              const hash = urlObj.hash.substring(1) // remove #
              const params = new URLSearchParams(hash)
              const access_token = params.get('access_token')
              const refresh_token = params.get('refresh_token')

              if (access_token && refresh_token) {
                const { data, error: setSessionError } = await supabase.auth.setSession({
                  access_token,
                  refresh_token
                })
                if (setSessionError) {
                  console.error('[AuthContext] Error setting session:', setSessionError)
                  throw setSessionError
                }
              }
            }
          } catch (error) {
            console.error('[AuthContext] Error handling auth redirect:', error)
          }
        }
      })
    }

    return () => {
      subscription.unsubscribe()
      if (appListener) {
        appListener.then(listener => listener.remove())
      }
    }
  }, [])

  // Without emailRedirectTo, Supabase falls back to its configured Site URL,
  // which still points at the Render staging host (dub-app.onrender.com).
  // Pin the confirmation link to the origin the user signed up from so
  // app.getdub.com signups don't bounce to Render.
  const confirmationRedirectTo = () => (
    Capacitor.isNativePlatform()
      ? 'com.getdub.app://auth/callback'
      : `${window.location.origin}/login`
  )

  const signUp = async (email, password, metadata = {}, { captchaToken } = {}) => {
    const emailRedirectTo = confirmationRedirectTo()

    // DUB-114: Supabase Auth is configured to require a Cloudflare Turnstile
    // token (Auth → Bot and Abuse Protection). The Register form captures the
    // token from the on-page widget; signups without it are rejected
    // server-side.
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo,
        captchaToken,
      }
    })
    if (error) throw error
    return data
  }

  const signIn = async (email, password, { captchaToken } = {}) => {
    // DUB-114: Supabase Auth → Bot Protection requires a captchaToken on every
    // session-creating call. Login page captures the token from the on-page
    // widget and threads it through.
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken },
    })
    if (error) throw error
    return data
  }

  // Ticket c492ca99: re-send the signup confirmation email for an account that
  // never confirmed. Supabase throws "Email not confirmed" on every login
  // attempt until the link in that email is clicked, and the original send is
  // the only one the signup flow ever produces — if it's lost or expired the
  // account is stranded. Used by the login screen (self-serve, after a failed
  // sign-in) and by admin user management.
  //
  // Auth → Bot Protection covers /resend the same way it covers signup and
  // login, so this needs a Turnstile token too. Rate limits are Supabase's
  // (per-email cooldown + project email quota); their message is surfaced
  // as-is by callers, since "wait 60 seconds" is the actionable part.
  const resendConfirmationEmail = async (email, { captchaToken } = {}) => {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: confirmationRedirectTo(),
        captchaToken,
      },
    })
    if (error) throw error
    return data
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })
    if (error) throw error
    return data
  }

  const resetPassword = async (email, { captchaToken } = {}) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken,
    })
    if (error) throw error
    return data
  }

  const signInWithGoogle = async () => {
    // Use custom URL scheme for native apps, web URL for browser
    const redirectTo = Capacitor.isNativePlatform()
      ? 'com.getdub.app://auth/callback'
      : `${window.location.origin}/login`
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        skipBrowserRedirect: Capacitor.isNativePlatform(), // Skip auto redirect on native
      },
    })
    
    if (error) {
      console.error('[AuthContext] Error initiating Google Sign-In:', error)
      throw error
    }
    
    // Manually open the OAuth URL in the system browser for native apps
    if (Capacitor.isNativePlatform() && data?.url) {
      await Browser.open({ url: data.url })
    }
    
    return data
  }

  const linkWithGoogle = async () => {
    const { data, error } = await supabase.auth.linkIdentity({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/settings`,
      },
    })
    if (error) throw error
    return data
  }

  // Get the list of linked identity providers for the current user
  const getLinkedProviders = () => {
    if (!user?.identities) return []
    return user.identities.map(identity => identity.provider)
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    resendConfirmationEmail,
    updatePassword,
    resetPassword,
    signInWithGoogle,
    linkWithGoogle,
    getLinkedProviders,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
